import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button, Flex, Loader, Text } from '@mantine/core';
import { DocumentService } from '@dealstack/frontend/shared/dataExchange/services/document/document.service';
import { useMutation } from '@tanstack/react-query';
import { DocumentViewerModalV2 } from '@dealstack/frontend/shared/components/DocumentViewerV2/DocumentViewerModalV2';
import { DocumentTable } from '@dealstack/frontend/shared/components/Documents/DocumentTable/DocumentTable';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { InlineLoader } from '@dealstack/frontend/shared/components/loaders/InlineLoader/InlineLoader';
import { createTextColumn } from '@dealstack/frontend/shared/components/Table/MantineTable/ColumnDefs/TextColumn';
import { useMantineTableDataOperations } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableDataOperations';
import { ExtendedColumnDef } from '@dealstack/frontend/shared/components/Table/MantineTable/useMantineTableWithPaginationFilteringSorting';
import {
	useTranslationDefault,
	useTranslationInvestor,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { ContractsReadinessStatus } from '@dealstack/shared/lib/common/business/contracts';
import { Tooltip } from '@dealstack/frontend/shared/components/Tooltip/Tooltip';
import { mapStatusToChipVariant, PreviewDocument, WetInkModalState } from './types';
import {
	areAllSignaturesFullyComplete,
	getPrimaryAddressCountry,
	isAllContractsSigned,
	isContractSignable,
	isFormRemovable,
	isFormSubmittable,
	isSigningComplete,
} from './utils';
import {
	DocumentSigningRoleType,
	DocumentSigningSecurityLevel,
	DocumentWorkflowInstanceSignatureStatus,
	DocumentWorkflowInstanceStatus,
	DocumentWorkflowTemplatePurpose,
	DocumentWorkflowTemplateType,
	ESignProvider,
	ReviewableContract,
	SignableContract,
	SignatureType,
	WitnessSigningData,
} from '@dealstack/shared/lib/generated';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { WetInkSigningModal } from '@dealstack/frontend/shared/components/WetInkSigningModal/WetInkSigningModal';
import { TEST_ELEMENT_IDS } from '@dealstack/shared/tests/testElementIds';
import { logger } from '@dealstack/frontend/shared/mechanism/logger/logger';
import {
	ContractWrapper,
	GeneratingContractsWrapper,
	StyledSignedChip,
} from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/styled';
import { useSignWetInkDocument } from '@dealstack/frontend/shared/modules/document/signing/hooks/useSignWetInkDocument';
import { useSignableDocuments } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useSignableDocuments';
import { SignableFormModal } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/components/SignableFormModal';
import { RemoveSubmittedFormAction } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/components/RemoveSubmittedFormAction';
import { useRemoveSubmittedForm } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useRemoveSubmittedForm';
import { InvalidPhoneNumberAlert } from '@dealstack/frontend/investor/pages/Documents/InvalidPhoneNumberAlert';
import { useDownloadPrefillableForm } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useDownloadPrefillableForm';
import { SIGNED_CHIP_VARIANT } from '@dealstack/frontend/shared/components/Chips/CommonChips';
import { useReviewDocument } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useReviewableDocument';
import {
	getFileExtensionFromMimeType,
	getValidFileNameForDownload,
} from '@dealstack/shared/lib/common/file/file';
import { WitnessEmailModal } from './WitnessEmailModal';
import { RepresentativeEmailModal } from './RepresentativeEmailModal';
import { ActionIconButton } from '@dealstack/frontend/shared/components/ActionIconButton/ActionIconButton';
import { useInviteWitness } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useInviteWitness';
import { useInviteRepresentative } from '@dealstack/frontend/investor/pages/Investment/common/DocumentWorkflowContracts/hooks/useInviteRepresentative';
import { EmptyState } from '@dealstack/frontend/shared/components/EmptyState/EmptyState.tsx';
import { useEidEasySign } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useEidEasySign';
import { EidEasySigningModal } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal';
import { EidEasySigningMethod } from '@dealstack/shared/lib/common/eideasy/signingMethods';
import {
	methodsForSecurityLevel,
	NORDIC_METHODS,
} from '@dealstack/shared/lib/common/eideasy/methodsForSecurityLevel';
import { webIsProduction } from '@dealstack/frontend/shared/environment';
import { useInvestor } from '@dealstack/frontend/investor/pages/Investment/hooks/useInvestor';
import { formatUserFullName } from '@dealstack/shared/lib/format/user';
import type { SignatureSuccessDetails } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/SignatureSuccessConfirmation/SignatureSuccessConfirmation.types';
import type { EidEasyCompleteRes } from '@dealstack/shared/lib/generated';
import { WitnessSigningStep } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/WitnessSigningStep';
import { WitnessSuccessStep } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/WitnessSuccessStep';
import { StepHeader } from '@dealstack/frontend/shared/components/EidEasySigningModal/steps/StepHeader';
import { EidEasySignStatus } from '@dealstack/frontend/shared/components/EidEasySigningModal/EidEasySigningModal.types';
import {
	anyRepresentativeRequiresWitness,
	resolveChainedPartyAction,
	resolveChainedPivAction,
	resolvePartySignAction,
	resolvePivSignAction,
} from './DocumentWorkflowContract.utils';
import type { ResolvedSignAction } from './DocumentWorkflowContract.utils';
import { LiveUpdatesPausedBanner } from '@dealstack/frontend/shared/components/LiveUpdatesPausedBanner/LiveUpdatesPausedBanner';
import { useLiveUpdatesPaused } from '@dealstack/frontend/shared/hooks/useLiveUpdatesPaused';

interface Props {
	setIsContinueDisabled: (disabled: boolean) => void;
	dealId: string;
	mipId: string;
	investorId: string;
	excludeTsaContracts: boolean;
	investmentWorkflowId?: string;
}

export const DocumentWorkflowContract = ({
	setIsContinueDisabled,
	dealId,
	mipId,
	investorId,
	excludeTsaContracts,
	investmentWorkflowId,
}: Props) => {
	const tInvestor = useTranslationInvestor();
	const tDefault = useTranslationDefault();
	const [signableFormModal, setSignableFormModal] = useState<WetInkModalState>({
		contract: null,
		opened: false,
		selectedFile: undefined,
		isSaving: false,
	});
	const [previewDocument, setPreviewDocument] = useState<PreviewDocument | null>(null);
	const [isPreviewModalOpen, setIsPreviewModalOpen] = useState(false);
	const [isFetchingPreview, setIsFetchingPreview] = useState(false);
	const [wetInkModal, setWetInkModal] = useState<WetInkModalState>({
		contract: null,
		opened: false,
		selectedFile: undefined,
		isSaving: false,
	});
	const getDownloadPreSignedUrl = useMutation({
		mutationFn: DocumentService.getDownloadPreSignedUrl,
	});
	const downloadPrefillableForm = useDownloadPrefillableForm();
	const reviewDocument = useReviewDocument();

	const { handleSigningDocumentWorkflow, wetInkSignDocumentWorkflowIsLoading } =
		useSignWetInkDocument({
			dealId,
			programmeId: mipId,
			documentWorkflowInstanceId: wetInkModal.contract?.documentWorkflowInstanceId ?? '',
		});

	const {
		status,
		contracts,
		reviewableContracts,
		friendlyMessage,
		isPhoneNumberInvalid,
		isLoading,
		refetch,
		generateDocuments,
		isLiveUpdatesConnected,
		isLiveUpdatesSupported,
		isGenerationTimedOut,
		canGenerateDocuments,
	} = useSignableDocuments({
		investmentWorkflowId,
		investorId,
		excludeTsaContracts,
	});

	const { removeSubmittedForm, removingInstanceId } = useRemoveSubmittedForm({
		programmeId: mipId,
		investorId,
		onRemoved: refetch,
	});

	const isLiveUpdatesPaused = useLiveUpdatesPaused({
		isConnected: isLiveUpdatesConnected,
		isSupported: isLiveUpdatesSupported,
	});

	// Witness and representative invitation state
	const [witnessModalState, setWitnessModalState] = useState<{
		contract: SignableContract;
		parentSignatureId: string;
	} | null>(null);
	const [repModalContract, setRepModalContract] = useState<SignableContract | null>(null);

	const inviteWitnessMutation = useInviteWitness();
	const inviteRepresentativeMutation = useInviteRepresentative();

	const { data: investorData } = useInvestor();

	const [activeSignFile, setActiveSignFile] = useState<{
		fileId: string;
		fileName: string;
		effectiveSecurityLevel: DocumentSigningSecurityLevel;
		signerRoleType: DocumentSigningRoleType;
		documentWorkflowInstanceId: string;
		parentSignatureId: string;
		requiresWitness: boolean;
		// True while signing the leg (PARTY or PIV) that was auto-continued after
		// the other, rather than started independently from its own column.
		isChainedContinuation: boolean;
		pivName: string | undefined;
	} | null>(null);
	// Bookkeeping for forcing the investor to sign both PARTY and PIV in the same
	// session, regardless of which column's button they start from. Refs (not
	// state) because they're read from the `onComplete` callback wired into
	// `useEidEasySign` below, which is declared before `startSigning` exists.
	const activeContractRef = useRef<SignableContract | null>(null);
	const chainedActionRef = useRef<ResolvedSignAction | null>(null);
	const continueChainRef = useRef<
		| ((
				contract: SignableContract,
				action: ResolvedSignAction,
				isChainedContinuation: boolean
		  ) => void)
		| null
	>(null);
	const [completionResult, setCompletionResult] = useState<EidEasyCompleteRes | null>(null);
	// When the active document requires a witness, the modal first shows a witness-email
	// step; signing only starts once the investor continues past it.
	const [witnessStepActive, setWitnessStepActive] = useState(false);
	const [witnessEmail, setWitnessEmail] = useState('');
	// Tracks whether the witness invite sent on signing completion failed (drives the
	// retry affordance on the success screen).
	const [witnessInviteFailed, setWitnessInviteFailed] = useState(false);
	// Holds the witness invite to send once the investor's own signature completes.
	const pendingWitnessRef = useRef<{
		documentWorkflowInstanceId: string;
		parentSignatureId: string;
		witnessEmail: string;
	} | null>(null);

	const {
		startSign: eidEasyStartSign,
		selectMethod: eidEasySelectMethod,
		goBack: eidEasyGoBack,
		confirmReview: eidEasyConfirmReview,
		confirmSmartId: eidEasyConfirmSmartId,
		confirmFreja: eidEasyConfirmFreja,
		confirmOtp: eidEasyConfirmOtp,
		submitOtpCode: eidEasySubmitOtpCode,
		resendOtpCode: eidEasyResendOtpCode,
		otpVerifying: eidEasyOtpVerifying,
		otpCodeError: eidEasyOtpCodeError,
		otpChannel: eidEasyOtpChannel,
		otpMaskedContact: eidEasyOtpMaskedContact,
		confirmSignature: eidEasyConfirmSignature,
		onSignatureConfirmed: eidEasyOnSignatureConfirmed,
		reset: eidEasyReset,
		status: eidEasyStatus,
		challengeCode: eidEasyChallengeCode,
		error: eidEasyError,
		selectedMethod: eidEasySelectedMethod,
		isSigningInProgress: eidEasyInProgress,
		isModalOpen: eidEasyModalOpen,
	} = useEidEasySign({
		dealId,
		programmeId: mipId,
		onComplete: (result) => {
			// Fire the witness invite for whichever leg just completed — this must
			// happen before chaining onward, since the next leg's own witness step
			// (if it requires one) will overwrite pendingWitnessRef with its own
			// data before this signature's invite would otherwise get sent.
			const pendingWitness = pendingWitnessRef.current;
			if (pendingWitness) {
				pendingWitnessRef.current = null;
				setWitnessInviteFailed(false);
				inviteWitnessMutation.mutate(
					{
						dealId,
						programmeId: mipId,
						...pendingWitness,
					},
					{ onError: () => setWitnessInviteFailed(true) }
				);
			}

			const chainedAction = chainedActionRef.current;
			const contract = activeContractRef.current;
			if (chainedAction && contract) {
				chainedActionRef.current = null;
				continueChainRef.current?.(contract, chainedAction, true);
				return;
			}

			void refetch();
			setCompletionResult(result ?? null);
		},
	});

	const handleInviteWitness = useCallback(
		(email: string) => {
			if (!witnessModalState) {
				return;
			}
			inviteWitnessMutation.mutate(
				{
					dealId,
					programmeId: mipId,
					documentWorkflowInstanceId:
						witnessModalState.contract.documentWorkflowInstanceId,
					parentSignatureId: witnessModalState.parentSignatureId,
					witnessEmail: email,
				},
				{
					onSuccess: () => {
						setWitnessModalState(null);
						void refetch();
						showToastV2({
							title: tInvestor('document_workflow.witness.success'),
							state: 'success',
						});
					},
					onError: () => {
						showToastV2({
							title: tInvestor('document_workflow.witness.error'),
							state: 'error',
						});
					},
				}
			);
		},
		[witnessModalState, dealId, mipId, inviteWitnessMutation, refetch, tInvestor]
	);

	const handleInviteRepresentative = useCallback(
		(email: string) => {
			if (!repModalContract || !repModalContract.representative?.signatureId) {
				return;
			}
			inviteRepresentativeMutation.mutate(
				{
					dealId,
					programmeId: mipId,
					documentWorkflowInstanceId: repModalContract.documentWorkflowInstanceId,
					signatureId: repModalContract.representative.signatureId,
					representativeEmail: email,
				},
				{
					onSuccess: () => {
						setRepModalContract(null);
						void refetch();
						showToastV2({
							title: tInvestor('document_workflow.representative.success'),
							state: 'success',
						});
					},
					onError: () => {
						showToastV2({
							title: tInvestor('document_workflow.representative.error'),
							state: 'error',
						});
					},
				}
			);
		},
		[repModalContract, dealId, mipId, inviteRepresentativeMutation, refetch, tInvestor]
	);

	const isInviting = inviteWitnessMutation.isPending || inviteRepresentativeMutation.isPending;

	const mapReviewableForDocumentsTable = useCallback(
		(reviewableContract: ReviewableContract): SignableContract => ({
			id: reviewableContract.id,
			documentWorkflowInstanceId: reviewableContract.id,
			status:
				reviewableContract.status === DocumentWorkflowInstanceStatus.COMPLETED
					? DocumentWorkflowInstanceSignatureStatus.COMPLETED
					: DocumentWorkflowInstanceSignatureStatus.PENDING_SIGNATURE,
			fileId: reviewableContract.fileId,
			fileName: reviewableContract.file.name,
			instruction: null,
			signatureType: SignatureType.REVIEW,
			eSignProvider: null,
			template: {
				type: reviewableContract.documentWorkflowTemplate.type,
				purpose: reviewableContract.documentWorkflowTemplate.purpose,
				fileId: '',
			},
			isSignableForm: false,
			formSubmitted: false,
			// A review-only row is never a signable form, so there is nothing to withdraw.
			canRemoveSubmittedForm: false,
			effectiveSecurityLevel: DocumentSigningSecurityLevel.ADVANCED,
			signerRoleType: DocumentSigningRoleType.PARTY,
		}),
		[]
	);

	const actionableContracts = useMemo(() => {
		const mappedReviewable = (reviewableContracts ?? []).map(mapReviewableForDocumentsTable);
		return [...(contracts ?? []), ...mappedReviewable];
	}, [contracts, reviewableContracts, mapReviewableForDocumentsTable]);

	/**
	 * This will trigger a call to the api to generate the signable documents
	 * The backend is idempotent, so it will not generate new documents if they already exist, unless the regenerate flag is set to true
	 */
	const hasGeneratedDocumentsCalledRef = useRef(false);
	useEffect(() => {
		if (hasGeneratedDocumentsCalledRef.current || !canGenerateDocuments) {
			return;
		}
		hasGeneratedDocumentsCalledRef.current = true;
		generateDocuments({ shouldRegenerateAllContracts: false });
	}, [canGenerateDocuments, generateDocuments]);

	const isGeneratingContracts = status === ContractsReadinessStatus.NOT_READY;
	const isNoContracts =
		status === ContractsReadinessStatus.READY &&
		(!contracts || contracts.length === 0) &&
		(!reviewableContracts || reviewableContracts.length === 0);
	const showTable =
		(status === ContractsReadinessStatus.READY && contracts && contracts.length > 0) ||
		(reviewableContracts && reviewableContracts?.length > 0);

	const hasEsignatureContracts = contracts?.some(
		(contract) => contract.signatureType === SignatureType.ELECTRONIC
	);

	// Manage continue button state — only the investor's own signing needs to be
	// complete (PARTY, and their own PIV leg where present — PIV is always
	// self-signed, see isAllContractsSigned). Witness/representative signatures
	// from other parties can still be pending (see the "waiting for additional
	// signatures" banner below).
	useEffect(() => {
		if (isNoContracts) {
			setIsContinueDisabled(false);
			return;
		}

		const allSigned = isAllContractsSigned(contracts);
		const allReviewed =
			!reviewableContracts?.length ||
			reviewableContracts.every(
				(rc) => rc.status === DocumentWorkflowInstanceStatus.COMPLETED
			);
		setIsContinueDisabled(!allSigned || !allReviewed);
	}, [contracts, reviewableContracts, isNoContracts, setIsContinueDisabled]);

	const dataOperations = useMantineTableDataOperations();
	// The PIV signer is always the same investor, so this column still surfaces their
	// outstanding PIV action even though it's the same person who signed as PARTY
	// (see startSigning's bidirectional chaining below).
	const hasPivSigning = useMemo(() => {
		return contracts?.some((contract) => !!contract.piv?.signatureId);
	}, [contracts]);

	const hasRepresentativeSigning = contracts?.some(
		(contract) => !!contract.representative?.signatureId
	);

	const hasRepresentativeWitnessRole = useMemo(
		() => anyRepresentativeRequiresWitness(contracts),
		[contracts]
	);

	const handlePreviewFile = useCallback(
		(
			fileId: string,
			fileName: string,
			documentWorkflowInstanceId: string | undefined,
			shouldPreview: boolean
		) => {
			if (shouldPreview) {
				setIsPreviewModalOpen(true);
				setIsFetchingPreview(true);
			}

			getDownloadPreSignedUrl.mutate(
				{ dealId, fileId, fileName },
				{
					onSuccess: async (data) => {
						if (!data.payload?.downloadUrl) {
							showToastV2({
								title: tInvestor('investment.join.contracts.preview_error'),
								state: 'error',
							});
							logger.error('No download URL received for file preview:', {
								dealId,
								fileId,
							});
							if (shouldPreview) {
								setIsPreviewModalOpen(false);
								setIsFetchingPreview(false);
							}
							return;
						}
						try {
							const response = await fetch(data.payload.downloadUrl);
							const blob = await response.blob();
							if (!response.ok) {
								throw new Error('Failed to fetch file blob');
							}
							const contentType = response.headers.get('content-type') || '';

							const fileExtension = getFileExtensionFromMimeType(contentType);

							if (shouldPreview) {
								setPreviewDocument({
									fileId,
									fileName,
									fileBlob: blob,
									fileExtension,
									documentWorkflowInstanceId,
								});
							} else {
								const url = window.URL.createObjectURL(blob);
								const link = document.createElement('a');
								link.href = url;
								const cleanFileName = getValidFileNameForDownload(
									fileName,
									fileExtension
								);

								link.setAttribute('download', cleanFileName);
								document.body.appendChild(link);
								link.click();
								document.body.removeChild(link);
								window.URL.revokeObjectURL(url);
							}
						} catch {
							showToastV2({
								title: tInvestor('investment.join.contracts.preview_error'),
								state: 'error',
							});
							logger.error('Error fetching file blob for preview:', {
								dealId,
								fileId,
							});
							if (shouldPreview) {
								setIsPreviewModalOpen(false);
							}
						} finally {
							if (shouldPreview) {
								setIsFetchingPreview(false);
							}
						}
					},
					onError: () => {
						showToastV2({
							title: tInvestor('investment.join.contracts.preview_error'),
							state: 'error',
						});
						logger.error('Error fetching pre-signed URL for file preview:', {
							dealId,
							fileId,
						});
						if (shouldPreview) {
							setIsPreviewModalOpen(false);
							setIsFetchingPreview(false);
						}
					},
				}
			);
		},
		[dealId, getDownloadPreSignedUrl, tInvestor]
	);

	const handleReviewFile = useCallback(
		(fileId: string, fileName: string, documentWorkflowInstanceId: string) => {
			handlePreviewFile(fileId, fileName, documentWorkflowInstanceId, true);
			reviewDocument.mutate(
				{ documentWorkflowInstanceId, fileId },
				{ onSuccess: () => refetch() }
			);
		},
		[handlePreviewFile, reviewDocument, refetch]
	);

	const handleOpenWetInkModal = useCallback((contract: SignableContract) => {
		setWetInkModal({
			contract,
			opened: true,
			selectedFile: undefined,
			isSaving: false,
		});
	}, []);

	const handleCloseWetInkModal = useCallback(() => {
		setWetInkModal({ contract: null, opened: false, selectedFile: undefined, isSaving: false });
	}, []);

	const handleWetInkSave = useCallback(async () => {
		if (!wetInkModal.selectedFile || !wetInkModal.contract) {
			return;
		}

		setWetInkModal((prev) => ({ ...prev, isSaving: true }));

		try {
			await handleSigningDocumentWorkflow({
				selectedFile: wetInkModal.selectedFile,
				signingRoleType: DocumentSigningRoleType.PARTY,
				investorId,
				onSuccess: () => {
					showToastV2({
						title: 'Wet ink signature uploaded successfully',
						state: 'success',
					});
				},
			});

			handleCloseWetInkModal();
			// Refresh contracts list
			void refetch();
		} catch (error) {
			showToastV2({
				title: 'Failed to upload wet ink signature',
				state: 'error',
			});
			logger.error('Error uploading wet ink signature:', error);
		} finally {
			setWetInkModal((prev) => ({ ...prev, isSaving: false }));
		}
	}, [
		wetInkModal.selectedFile,
		wetInkModal.contract,
		handleSigningDocumentWorkflow,
		investorId,
		handleCloseWetInkModal,
		refetch,
	]);

	const handleDownloadContract = useCallback(() => {
		if (!wetInkModal.contract) {
			return;
		}
		handlePreviewFile(
			wetInkModal.contract.fileId,
			wetInkModal.contract.fileName,
			wetInkModal.contract.documentWorkflowInstanceId,
			false
		);
	}, [wetInkModal.contract, handlePreviewFile]);

	const handleContractAction = useCallback(() => {
		if (!wetInkModal.contract) {
			return;
		}
		const isPrefillable =
			wetInkModal.contract.template.purpose ===
			DocumentWorkflowTemplatePurpose.PRE_FILLABLE_FORM;

		if (isPrefillable) {
			downloadPrefillableForm.mutate({
				fileId: wetInkModal.contract.template.fileId,
				instanceSignatureId: wetInkModal.contract.id,
			});
			return;
		}
		handleDownloadContract();
	}, [wetInkModal.contract, handleDownloadContract, downloadPrefillableForm]);

	const handleFileSelect = useCallback((file: File) => {
		setWetInkModal((prev) => ({ ...prev, selectedFile: file }));
	}, []);

	const handleFileRemove = useCallback(() => {
		setWetInkModal((prev) => ({ ...prev, selectedFile: undefined }));
	}, []);

	const handleOpenSignableFormModal = useCallback((contract: SignableContract) => {
		setSignableFormModal({
			contract,
			opened: true,
			selectedFile: undefined,
			isSaving: false,
		});
	}, []);

	const renderWitnessCell = useCallback(
		(
			contract: SignableContract,
			witnessData: WitnessSigningData | undefined,
			parentSignatureId: string | undefined,
			parentSignatureStatus: DocumentWorkflowInstanceSignatureStatus | null | undefined
		) => {
			if (
				contract.signatureType === SignatureType.REVIEW ||
				contract.signatureType === SignatureType.WET_INK ||
				!witnessData?.hasWitnessRole ||
				!parentSignatureId
			) {
				return null;
			}
			if (!isSigningComplete(parentSignatureStatus)) {
				return (
					<Text size="sm" c="dimmed">
						{tInvestor('document_workflow.witness.sign_first')}
					</Text>
				);
			}
			const hasWitnessCompleted = isSigningComplete(witnessData?.status);
			if (hasWitnessCompleted) {
				return <StyledSignedChip variant={SIGNED_CHIP_VARIANT.SIGNED} />;
			}
			const hasBeenInvited = !!witnessData?.signatureId;
			return (
				<Button
					data-testid={TEST_ELEMENT_IDS.INVESTOR_WITNESS_INVITE_BUTTON}
					size="md"
					variant={hasBeenInvited ? 'subtle' : 'primary'}
					leftSection={<Icon variant="mail" size="md" />}
					onClick={() => setWitnessModalState({ contract, parentSignatureId })}
				>
					{hasBeenInvited
						? tInvestor('document_workflow.witness.resend')
						: tInvestor('document_workflow.witness.email_button')}
				</Button>
			);
		},
		[tInvestor]
	);

	const startSigning = useCallback(
		(contract: SignableContract, action: ResolvedSignAction, isChainedContinuation = false) => {
			activeContractRef.current = contract;
			// The investor is always both PARTY and PIV signer for eID Easy contracts, so
			// whichever leg they start from, chain into the other if it's still pending.
			// Only derive this on the initiating call — `contract` here is a stale snapshot
			// from before this session started, so re-deriving on the continuation call
			// (isChainedContinuation) would incorrectly chain back onto the leg that was
			// just signed, since its local status hasn't been refetched yet.
			if (isChainedContinuation) {
				chainedActionRef.current = null;
			} else if (action.role === DocumentSigningRoleType.PARTY) {
				chainedActionRef.current = resolveChainedPivAction(contract);
			} else if (action.role === DocumentSigningRoleType.PIV) {
				chainedActionRef.current = resolveChainedPartyAction(contract);
			} else {
				chainedActionRef.current = null;
			}

			const effectiveSecurityLevel =
				action.role === DocumentSigningRoleType.PIV
					? contract.piv?.effectiveSecurityLevel ?? contract.effectiveSecurityLevel
					: contract.effectiveSecurityLevel;
			setActiveSignFile({
				fileId: contract.fileId,
				fileName: contract.fileName,
				effectiveSecurityLevel,
				signerRoleType: action.role,
				documentWorkflowInstanceId: contract.documentWorkflowInstanceId,
				parentSignatureId: action.parentSignatureId,
				requiresWitness: action.requiresWitness,
				isChainedContinuation,
				pivName: contract.piv?.pivName,
			});
			if (action.requiresWitness) {
				setWitnessEmail(action.witnessEmail ?? '');
				setWitnessStepActive(true);
			} else {
				eidEasyStartSign(
					action.parentSignatureId,
					contract.documentWorkflowInstanceId,
					contract.fileId
				);
			}
		},
		[eidEasyStartSign]
	);
	continueChainRef.current = startSigning;

	const handleWitnessContinue = useCallback(
		(email: string) => {
			if (!activeSignFile) {
				return;
			}
			setWitnessEmail(email);
			pendingWitnessRef.current = {
				documentWorkflowInstanceId: activeSignFile.documentWorkflowInstanceId,
				parentSignatureId: activeSignFile.parentSignatureId,
				witnessEmail: email,
			};
			setWitnessStepActive(false);
			eidEasyStartSign(
				activeSignFile.parentSignatureId,
				activeSignFile.documentWorkflowInstanceId,
				activeSignFile.fileId
			);
		},
		[activeSignFile, eidEasyStartSign]
	);

	// Cancel the in-flight signing and return to the witness-configuration step.
	const handleBackToWitness = useCallback(() => {
		eidEasyReset();
		setWitnessStepActive(true);
	}, [eidEasyReset]);

	// Retry the witness invite from the success screen after a failed send.
	const handleResendWitnessInvite = useCallback(() => {
		if (!activeSignFile) {
			return;
		}
		setWitnessInviteFailed(false);
		inviteWitnessMutation.mutate(
			{
				dealId,
				programmeId: mipId,
				documentWorkflowInstanceId: activeSignFile.documentWorkflowInstanceId,
				parentSignatureId: activeSignFile.parentSignatureId,
				witnessEmail,
			},
			{ onError: () => setWitnessInviteFailed(true) }
		);
	}, [activeSignFile, witnessEmail, dealId, mipId, inviteWitnessMutation]);

	const closeSigningModal = useCallback(() => {
		eidEasyReset();
		setActiveSignFile(null);
		setCompletionResult(null);
		setWitnessStepActive(false);
		setWitnessEmail('');
		setWitnessInviteFailed(false);
		pendingWitnessRef.current = null;
		activeContractRef.current = null;
		chainedActionRef.current = null;
	}, [eidEasyReset]);

	const renderEidEasySignAction = useCallback(
		(contract: SignableContract, action: ResolvedSignAction) => {
			if (action.kind === 'NONE') {
				return null;
			}

			if (action.kind === 'WITNESS_COMPLETE') {
				return (
					<Flex direction="column" gap="2px">
						<StyledSignedChip variant={SIGNED_CHIP_VARIANT.SIGNED} />
					</Flex>
				);
			}

			if (action.kind === 'SIGNED') {
				return (
					<Flex direction="column" gap="2px">
						<StyledSignedChip variant={SIGNED_CHIP_VARIANT.SIGNED} />
					</Flex>
				);
			}

			if (action.kind === 'RESEND_WITNESS') {
				return (
					<Flex direction="column" gap="2px" align="flex-start">
						<Button
							size="md"
							variant="subtle"
							leftSection={<Icon variant="mail" size="md" />}
							loading={inviteWitnessMutation.isPending}
							onClick={() => {
								if (!action.witnessEmail) {
									return;
								}
								inviteWitnessMutation.mutate(
									{
										dealId,
										programmeId: mipId,
										documentWorkflowInstanceId:
											contract.documentWorkflowInstanceId,
										parentSignatureId: action.parentSignatureId,
										witnessEmail: action.witnessEmail,
									},
									{
										onSuccess: () =>
											showToastV2({
												title: tInvestor(
													'document_workflow.witness.resent'
												),
											}),
									}
								);
							}}
						>
							{tInvestor('document_workflow.witness.resend')}
						</Button>
					</Flex>
				);
			}

			return (
				<Button
					size="md"
					variant="subtle"
					data-testid={TEST_ELEMENT_IDS.INVESTOR_DOCUMENT_SIGN_BUTTON}
					// A self-representing PIV investor sees a PARTY and a PIV sign button in the same
					// row (both carry the same testid); this discriminator lets tests target one.
					data-signing-role={action.role}
					// Lets tests target one document's button when the table lists more than one —
					// the row's own testid is keyed off the signature document, not this id.
					data-document-workflow-instance-id={contract.documentWorkflowInstanceId}
					onClick={() => startSigning(contract, action)}
				>
					{tInvestor('investment.join.contracts.sign')}
				</Button>
			);
		},
		[inviteWitnessMutation, dealId, mipId, tInvestor, startSigning]
	);

	const columns: ExtendedColumnDef<SignableContract>[] = useMemo(
		() => [
			createTextColumn({
				id: 'name',
				headerText: tInvestor('investment.join.contracts.column.name'),
				accessorKey: 'fileId',
				enableSorting: false,
				cell: ({ row }) => (
					<Flex align="center" gap="4px" style={{ overflow: 'hidden' }}>
						<Icon variant="document-v3-rounded" size="lg" style={{ flexShrink: 0 }} />
						<Tooltip tooltip={row.original.fileName} hoverChildrenInsteadIcon>
							<Text size="sm" lineClamp={2} style={{ wordBreak: 'break-word' }}>
								{row.original.fileName}
							</Text>
						</Tooltip>
					</Flex>
				),
			}),
			{
				id: 'status',
				header: () => (
					<Text fw={600}>{tInvestor('investment.join.contracts.column.status')}</Text>
				),
				enableSorting: false,
				enableColumnFilter: false,
				size: 100,
				cell: ({ row }) => {
					let variant: SIGNED_CHIP_VARIANT;
					if (row.original.template.type !== DocumentWorkflowTemplateType.READ_ONLY) {
						variant = mapStatusToChipVariant(
							row.original.status,
							row.original.signatureType
						);
					} else {
						variant = SIGNED_CHIP_VARIANT.NOT_APPLICABLE;
						if (
							row.original.status ===
							DocumentWorkflowInstanceSignatureStatus.COMPLETED
						) {
							variant = SIGNED_CHIP_VARIANT.COMPLETE;
						}
					}
					return <StyledSignedChip variant={variant} />;
				},
			},
			{
				id: 'investor_sign_link',
				header: () => (
					<Text fw={600}>{tInvestor('investment.join.contracts.column.action')}</Text>
				),
				enableSorting: false,
				enableColumnFilter: false,
				size: 100,
				cell: ({ row }) => {
					const contract = row.original;
					const canSign = isContractSignable(contract);

					if (contract.signatureType === SignatureType.REVIEW) {
						const hasReviewCompleted =
							contract.status === DocumentWorkflowInstanceSignatureStatus.COMPLETED;
						return (
							<Flex gap="xs">
								<Button
									size="md"
									variant="subtle"
									disabled={hasReviewCompleted}
									onClick={() => {
										handleReviewFile(
											contract.fileId,
											contract.fileName,
											contract.documentWorkflowInstanceId
										);
									}}
								>
									{hasReviewCompleted
										? tInvestor('investment.join.contracts.review_completed')
										: tInvestor('investment.join.contracts.review')}
								</Button>
							</Flex>
						);
					}

					const hasSigningCompleted = isSigningComplete(contract.status);
					const canSubmitForm = isFormSubmittable(contract);

					return (
						<Flex gap="xs">
							{isFormRemovable(contract) && (
								<RemoveSubmittedFormAction
									isLoading={
										removingInstanceId === contract.documentWorkflowInstanceId
									}
									onConfirm={() =>
										removeSubmittedForm(contract.documentWorkflowInstanceId)
									}
								/>
							)}
							{contract.isSignableForm && !contract.formSubmitted && (
								<Button
									size="md"
									variant="subtle"
									disabled={!canSubmitForm}
									onClick={(e) => {
										e.preventDefault();
										e.stopPropagation();
										handleOpenSignableFormModal(contract);
									}}
								>
									{tInvestor(
										'investment.contracts.signableFormModal.submit_form'
									)}
								</Button>
							)}
							{contract.signatureType === SignatureType.ELECTRONIC &&
								contract.eSignProvider === ESignProvider.EID_EASY &&
								(!contract.isSignableForm || contract.formSubmitted) &&
								renderEidEasySignAction(contract, resolvePartySignAction(contract))}
							{contract.signatureType === SignatureType.WET_INK &&
								[
									DocumentWorkflowTemplateType.SIGNATURE_REQUIRED,
									DocumentWorkflowTemplateType.FORM_INPUT,
								].includes(contract.template.type) && (
									<Button
										size="md"
										variant="subtle"
										disabled={!canSign || hasSigningCompleted}
										onClick={(e) => {
											e.preventDefault();
											e.stopPropagation();
											handleOpenWetInkModal(contract);
										}}
									>
										{contract.template.type ===
											DocumentWorkflowTemplateType.SIGNATURE_REQUIRED &&
											(hasSigningCompleted
												? tInvestor(
														'investment.join.contracts.sign_completed'
													)
												: tInvestor('investment.join.contracts.sign'))}
										{contract.template.type ===
											DocumentWorkflowTemplateType.FORM_INPUT &&
											tInvestor('investment.join.contracts.complete')}
									</Button>
								)}
						</Flex>
					);
				},
			},
			...(hasPivSigning
				? [
						{
							id: 'piv_sign_link',
							header: () => (
								<Text fw={600}>
									{tInvestor('investment.join.contracts.column.piv_sign_link')}
								</Text>
							),
							enableSorting: false,
							enableColumnFilter: false,
							size: 200,
							cell: ({ row }) => {
								const contract = row.original;

								if (
									contract.signatureType !== SignatureType.ELECTRONIC ||
									!contract.piv?.signatureId
								) {
									return null;
								}

								// eID Easy PIV rows are always self-signed by the investor, but
								// still shown here as their own action item — starting from this
								// button chains into the PARTY leg too if that's still pending
								// (see startSigning's bidirectional chaining).
								return (
									<Flex gap="xs">
										{renderEidEasySignAction(
											contract,
											resolvePivSignAction(contract)
										)}
									</Flex>
								);
							},
						},
					]
				: []),
			...(hasRepresentativeSigning
				? [
						{
							id: 'representative_copy_link',
							header: () => (
								<Flex align="center" h="100%">
									<Text size="sm" fw={600}>
										{tInvestor(
											'investment.join.contracts.column.representative_copy_link'
										)}
									</Text>
									<Tooltip
										tooltip={tInvestor(
											'investment.join.contracts.row.copy_link_tooltip_default'
										)}
										iconSize="lg"
									/>
								</Flex>
							),
							enableSorting: false,
							columnVisibility: true,
							enableColumnFilter: false,
							size: 220,
							cell: ({ row }) => {
								const contract = row.original;

								if (contract.signatureType === SignatureType.REVIEW) {
									return null;
								}

								const hasSigningCompleted = isSigningComplete(
									contract.representative?.status
								);

								// eID Easy: email representative button
								if (contract.representative?.signatureId) {
									if (hasSigningCompleted) {
										return (
											<StyledSignedChip
												variant={SIGNED_CHIP_VARIANT.SIGNED}
											/>
										);
									}

									const hasBeenEmailed = !!contract.representative?.inviteSent;

									return (
										<Button
											size="md"
											variant={hasBeenEmailed ? 'subtle' : 'primary'}
											leftSection={<Icon variant="mail" size="md" />}
											onClick={() => setRepModalContract(contract)}
										>
											{hasBeenEmailed
												? tInvestor(
														'document_workflow.representative.resend'
													)
												: tInvestor(
														'document_workflow.representative.email_button'
													)}
										</Button>
									);
								}

								return null;
							},
						},
					]
				: []),
			...(hasRepresentativeSigning && hasRepresentativeWitnessRole
				? [
						{
							id: 'representative_witness',
							header: () => (
								<Text
									size="sm"
									fw={600}
									data-testid={
										TEST_ELEMENT_IDS.INVESTOR_REPRESENTATIVE_WITNESS_COLUMN
									}
								>
									{tInvestor(
										'investment.join.contracts.column.representative_witness'
									)}
								</Text>
							),
							enableSorting: false,
							enableColumnFilter: false,
							size: 200,
							cell: ({ row }: { row: { original: SignableContract } }) =>
								renderWitnessCell(
									row.original,
									row.original.representative?.witness,
									row.original.representative?.signatureId,
									row.original.representative?.status
								),
						},
					]
				: []),
			{
				id: 'view',
				header: () => (
					<Text fw={600}>{tInvestor('investment.join.contracts.column.view')}</Text>
				),
				enableSorting: false,
				enableColumnFilter: false,
				enableResizing: false,
				size: 80,
				minSize: 80,
				maxSize: 80,
				cell: ({ row }) => (
					<Flex justify="center" align="center">
						<ActionIconButton
							iconVariant="eye"
							onClick={() =>
								handlePreviewFile(
									row.original.fileId,
									row.original.fileName,
									row.original.documentWorkflowInstanceId,
									true
								)
							}
						/>
					</Flex>
				),
			},
		],
		[
			tInvestor,
			hasPivSigning,
			hasRepresentativeSigning,
			handlePreviewFile,
			handleReviewFile,
			handleOpenWetInkModal,
			handleOpenSignableFormModal,
			removingInstanceId,
			removeSubmittedForm,
			refetch,
			hasRepresentativeWitnessRole,
			renderWitnessCell,
			renderEidEasySignAction,
		]
	);

	if (isLoading && !status) {
		return <InlineLoader />;
	}

	if (isPhoneNumberInvalid && hasEsignatureContracts) {
		return (
			<InvalidPhoneNumberAlert
				onRegenerate={() => generateDocuments({ shouldRegenerateAllContracts: true })}
			/>
		);
	}

	const signerFullName =
		formatUserFullName({
			firstName: investorData?.user.firstName,
			lastName: investorData?.user.lastName,
		}) || null;

	const signingSuccessDetails: SignatureSuccessDetails | undefined = activeSignFile
		? {
				signerName: signerFullName,
				signingMethod: completionResult?.signingMethod ?? null,
				signedAt: completionResult?.signedAt ?? null,
				session: completionResult?.session,
				isDownloading: getDownloadPreSignedUrl.isPending,
				onDownloadSignedPdf: () => {
					const fileId = completionResult?.fileId ?? activeSignFile.fileId;
					handlePreviewFile(fileId, activeSignFile.fileName, undefined, false);
				},
			}
		: undefined;

	// Shown throughout the leg that was auto-continued from the other one, so the
	// investor understands why the witness step / signing method screen appeared
	// a second time. `witnessStepActive` overrides the DONE check below: while the
	// continuation's witness step is active, `eidEasyStatus` is still stuck at DONE
	// from the signature that just completed (eidEasyStartSign isn't called again
	// until the witness step is submitted), so excluding DONE alone would hide
	// the banner for the entire witness step instead of just the final success screen.
	const chainedSigningBanner =
		activeSignFile?.isChainedContinuation &&
		(witnessStepActive || eidEasyStatus !== EidEasySignStatus.DONE) ? (
			<Alert color="blue" mb="sm">
				{tInvestor(
					activeSignFile.signerRoleType === DocumentSigningRoleType.PIV
						? 'document_workflow.piv.sign_as_piv_banner'
						: 'document_workflow.piv.sign_as_party_banner',
					{ pivName: activeSignFile.pivName }
				)}
			</Alert>
		) : undefined;

	// The signing modal's panel slot: the witness-config step before signing, then the
	// witness success screen once a witness-required signature completes.
	let witnessPanelOverride: React.ReactNode;
	if (witnessStepActive) {
		witnessPanelOverride = (
			<>
				{chainedSigningBanner}
				<WitnessSigningStep
					defaultEmail={witnessEmail}
					isSubmitting={inviteWitnessMutation.isPending}
					onContinue={handleWitnessContinue}
				/>
			</>
		);
	} else if (eidEasyStatus === EidEasySignStatus.DONE && activeSignFile?.requiresWitness) {
		witnessPanelOverride = (
			<WitnessSuccessStep
				witnessEmail={witnessEmail}
				inviteFailed={witnessInviteFailed}
				isResending={inviteWitnessMutation.isPending}
				onResend={handleResendWitnessInvite}
				onDone={() => {
					closeSigningModal();
					void refetch();
				}}
			/>
		);
	}

	// During the entry signing steps (which have no eID back of their own), offer a
	// Back control to return to the witness-configuration step.
	const witnessBackHeader =
		activeSignFile?.requiresWitness &&
		(eidEasyStatus === EidEasySignStatus.METHOD_SELECT ||
			eidEasyStatus === EidEasySignStatus.DOCUMENT_REVIEW) ? (
			<StepHeader onGoBack={handleBackToWitness} />
		) : undefined;

	const witnessPanelHeader =
		chainedSigningBanner || witnessBackHeader ? (
			<>
				{chainedSigningBanner}
				{witnessBackHeader}
			</>
		) : undefined;

	const totalRowCount = (contracts?.length ?? 0) + (reviewableContracts?.length ?? 0);

	return (
		<ContractWrapper>
			<LiveUpdatesPausedBanner
				isPaused={isLiveUpdatesPaused}
				isSupported={isLiveUpdatesSupported}
				onRefresh={refetch}
			/>
			<Flex w="100%" direction="row-reverse" gap="sm" mb="md">
				<Button
					loading={isLoading}
					onClick={() => refetch()}
					variant="default"
					fw={400}
					leftSection={<Icon variant="refresh-v3" size="lg" />}
				>
					{tInvestor('investment.join.contracts.refresh_button')}
				</Button>
			</Flex>
			{isGeneratingContracts && (
				<GeneratingContractsWrapper>
					{isGenerationTimedOut ? (
						<>
							<Text fw={500} size="md">
								{tInvestor('investment.join.contracts.generation_timeout_title')}
							</Text>
							<Text c="dimmed" size="sm" mt="xs">
								{tInvestor('investment.join.contracts.generation_timeout_message')}
							</Text>
							<Button
								onClick={() =>
									generateDocuments({ shouldRegenerateAllContracts: true })
								}
								variant="subtle"
								size="sm"
								mt="md"
								rightSection={<Icon variant="refresh-v3" size="lg" />}
							>
								{tInvestor('investment.join.contracts.regenerate_button')}
							</Button>
						</>
					) : (
						<>
							<div>
								<InlineLoader />
							</div>
							<Text>
								{friendlyMessage
									? tInvestor(friendlyMessage as Parameters<typeof tInvestor>[0])
									: tInvestor('investment.join.contracts.wait_while_generating')}
							</Text>
						</>
					)}
				</GeneratingContractsWrapper>
			)}
			{isNoContracts && (
				<EmptyState
					icon="es-hammock"
					title={tDefault('no_data')}
					description={tInvestor('no_contracts')}
				/>
			)}
			{showTable && (
				<div style={{ position: 'relative' }}>
					<DocumentTable
						data={actionableContracts}
						columns={columns}
						dataOperations={dataOperations}
						rowCount={totalRowCount}
						enablePagination={false}
						enableRowSelection={false}
						hasNoTopToolbar={true}
						enableTopToolbar={false}
						getRowId={(row) => row.id}
					/>

					{/* Instructions overlay positioned after the table */}
					{contracts?.map((contract, index) => {
						const hasInstructions = contract.instruction && contract.instruction.trim();

						if (!hasInstructions) {
							return null;
						}

						return (
							<div
								key={`instructions-${contract.id}`}
								style={{
									backgroundColor: '#f8f9fa',
									padding: '16px',
									border: '1px solid #e9ecef',
									borderRadius: '4px',
									margin: '8px 0',
									marginTop: index === 0 ? '8px' : '4px',
								}}
							>
								<Flex direction="column" gap="sm">
									<Text fw={500} size="sm" c="dimmed">
										Instructions for {contract.fileName}
									</Text>
									<Text
										size="sm"
										style={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}
									>
										{contract.instruction}
									</Text>
								</Flex>
							</div>
						);
					})}
				</div>
			)}
			{isAllContractsSigned(contracts) && !areAllSignaturesFullyComplete(contracts) && (
				<Alert color="blue" mt="md">
					{tInvestor('investment.join.contracts.waiting_for_additional_signatures')}
				</Alert>
			)}
			<DocumentViewerModalV2
				opened={isPreviewModalOpen}
				file={previewDocument?.fileBlob}
				fileName={previewDocument?.fileName ?? ''}
				fileExtension={previewDocument?.fileExtension}
				isLoading={isFetchingPreview || !previewDocument}
				onClose={() => {
					setIsPreviewModalOpen(false);
					setPreviewDocument(null);
				}}
			/>
			{wetInkModal.opened && (
				<React.Suspense fallback={<Loader />}>
					{(() => {
						// there seems to be an issue with lazy loading the WetInkSigningModal causing the whole screen to re render and showing a full loader, this fixes it
						try {
							return (
								<WetInkSigningModal
									onSave={handleWetInkSave}
									onClose={handleCloseWetInkModal}
									isSaving={
										wetInkModal.isSaving || wetInkSignDocumentWorkflowIsLoading
									}
									onDownloadContract={handleContractAction}
									onFileRemove={handleFileRemove}
									onFileSelect={handleFileSelect}
									opened={wetInkModal.opened}
									selectedFile={wetInkModal.selectedFile}
									isPrefillable={
										wetInkModal.contract?.template.type ===
										DocumentWorkflowTemplateType.FORM_INPUT
									}
								/>
							);
						} catch (error) {
							logger.error('WetInkSigningModal error:', error);
							return <div></div>;
						}
					})()}
				</React.Suspense>
			)}
			<WitnessEmailModal
				isOpen={!!witnessModalState}
				isLoading={isInviting}
				initialEmail={
					witnessModalState
						? (() => {
								const c = witnessModalState.contract;
								const sigId = witnessModalState.parentSignatureId;
								if (sigId === c.id) {
									return c.witness?.email;
								}
								if (sigId === c.piv?.signatureId) {
									return c.piv?.witness?.email;
								}
								if (sigId === c.representative?.signatureId) {
									return c.representative?.witness?.email;
								}
								return undefined;
							})()
						: undefined
				}
				onClose={() => setWitnessModalState(null)}
				onSubmit={handleInviteWitness}
			/>
			<RepresentativeEmailModal
				isOpen={!!repModalContract}
				isLoading={isInviting}
				initialEmail={repModalContract?.representative?.email ?? undefined}
				onClose={() => setRepModalContract(null)}
				onSubmit={handleInviteRepresentative}
			/>
			<EidEasySigningModal
				status={eidEasyStatus}
				challengeCode={eidEasyChallengeCode}
				error={eidEasyError}
				selectedMethod={eidEasySelectedMethod}
				isSigningInProgress={eidEasyInProgress}
				isModalOpen={eidEasyModalOpen || witnessStepActive}
				panelOverride={witnessPanelOverride}
				panelHeader={witnessPanelHeader}
				disableSavedSignature={
					activeSignFile?.signerRoleType === DocumentSigningRoleType.WITNESS
				}
				allowedMethods={
					activeSignFile
						? methodsForSecurityLevel(
								activeSignFile.effectiveSecurityLevel,
								activeSignFile.signerRoleType,
								{ enableSimpleSign: !webIsProduction }
							)
						: [
								EidEasySigningMethod.SMART_ID,
								EidEasySigningMethod.FTN,
								EidEasySigningMethod.OTP,
								...NORDIC_METHODS,
							]
				}
				restrictToCountry
				countryCode={getPrimaryAddressCountry(
					activeSignFile?.signerRoleType === DocumentSigningRoleType.PIV
						? investorData?.pivPerson ?? investorData?.draftPivPerson
						: investorData?.person
				)}
				defaultPhone={investorData?.user.phoneNumber ?? undefined}
				defaultPhonePrefix={investorData?.user.telephonePrefix ?? undefined}
				defaultEmail={investorData?.user.email ?? undefined}
				// Once signing completes, preview the freshly signed document
				fileId={completionResult?.fileId ?? activeSignFile?.fileId}
				fileName={activeSignFile?.fileName}
				successDetails={signingSuccessDetails}
				onConfirmReview={eidEasyConfirmReview}
				onClose={closeSigningModal}
				onSelectMethod={eidEasySelectMethod}
				onGoBack={eidEasyGoBack}
				onConfirmSmartId={(idcode, countryCode) => {
					void eidEasyConfirmSmartId(idcode, countryCode);
				}}
				onConfirmSignature={(signatureDataUrl, source) => {
					void eidEasyConfirmSignature(signatureDataUrl, source);
				}}
				onSignatureConfirmed={(sig) => {
					void eidEasyOnSignatureConfirmed(sig);
				}}
				onConfirmFreja={eidEasyConfirmFreja}
				onConfirmOtp={eidEasyConfirmOtp}
				onSubmitOtpCode={(code) => {
					void eidEasySubmitOtpCode(code);
				}}
				onResendOtpCode={eidEasyResendOtpCode}
				otpVerifying={eidEasyOtpVerifying}
				otpCodeError={eidEasyOtpCodeError}
				otpChannel={eidEasyOtpChannel}
				otpMaskedContact={eidEasyOtpMaskedContact}
			/>
			{signableFormModal.opened && (
				<SignableFormModal
					dealId={dealId}
					mipId={mipId}
					investorId={investorId}
					signableFormModal={signableFormModal}
					setSignableFormModal={setSignableFormModal}
					handlePreviewFile={handlePreviewFile}
					refetch={refetch}
				/>
			)}
		</ContractWrapper>
	);
};
